import React, { useCallback, useEffect, useState } from "react";
import { api, fmtPKR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../../components/ui/dialog";
import { Banknote, Hourglass, Wallet as WalletIcon, ArrowDownToLine, Building2, Save, MessageCircle, ExternalLink, Share2 } from "lucide-react";
import { toast } from "sonner";
import WaStatusPill from "../../components/WaStatusPill";

const STATUS_TONE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

const BANK_FIELDS = [
  { key: "account_holder", label: "Account holder name" },
  { key: "bank_name", label: "Bank name" },
  { key: "account_number", label: "Account number / IBAN" },
  { key: "branch", label: "Branch (optional)" },
  { key: "jazzcash_number", label: "JazzCash number (optional)" },
  { key: "easypaisa_number", label: "Easypaisa number (optional)" },
];

export default function DistributorWallet() {
  const [data, setData] = useState(null);
  const [bank, setBank] = useState({});
  const [me, setMe] = useState(null);
  const [waNumber, setWaNumber] = useState("");
  const [waBusy, setWaBusy] = useState(false);
  const [bankBusy, setBankBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(
    () => api.get("/distributors/wallet").then((r) => setData(r.data)),
    [],
  );
  const loadMe = useCallback(
    () => api.get("/distributors/me").then((r) => {
      setMe(r.data);
      setBank(r.data?.bank || {});
      setWaNumber(r.data?.whatsapp_number || "");
    }),
    [],
  );

  useEffect(() => { load(); loadMe(); }, [load, loadMe]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/distributors/withdrawals", { amount: Number(amount), note });
      toast.success("Withdrawal request submitted");
      setOpen(false);
      setAmount(""); setNote("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const saveBank = async (e) => {
    e.preventDefault();
    setBankBusy(true);
    try {
      const r = await api.put("/distributors/me/bank", bank);
      setBank(r.data?.bank || {});
      toast.success("Bank details saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBankBusy(false);
    }
  };

  const saveWa = async (e) => {
    e.preventDefault();
    setWaBusy(true);
    try {
      const r = await api.put("/distributors/me/whatsapp", { whatsapp_number: waNumber });
      setMe(r.data);
      setWaNumber(r.data?.whatsapp_number || "");
      toast.success("WhatsApp number saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setWaBusy(false);
    }
  };

  const referralLink = me?.referral_code
    ? `https://myhisaabpro.com/?ref=${me.referral_code}`
    : "https://myhisaabpro.com/";

  const shareReferralViaWhatsApp = () => {
    const text = `Apni shop ka installment hisaab strong banayein. HisaabPro try karein 30 din free — ${referralLink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const openSupport = () => {
    window.open(
      "https://wa.me/923718067478?text=" +
        encodeURIComponent("Assalam-o-Alaikum, mujhe HisaabPro distributor support chahiye."),
      "_blank"
    );
  };

  const waConnected = /^\d{10,15}$/.test((waNumber || "").replace(/\D/g, ""));

  if (!data) return <div className="text-slate-500">Loading…</div>;

  const bankComplete = bank.account_holder && bank.bank_name && bank.account_number;

  return (
    <div className="space-y-6" data-testid="dist-wallet-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Wallet & Bank Details</h1>
        <p className="text-slate-500 mt-1">Aap ka commission balance, bank info aur withdraw history.</p>
      </div>

      {!bankComplete && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm" data-testid="dist-bank-warning">
          <div className="font-semibold text-amber-800">Bank details adhoore hain</div>
          <div className="text-amber-700">Withdrawals process hone ke liye account holder name, bank aur account number zaroori hain.</div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="w-10 h-10 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-700 flex items-center justify-center"><Banknote className="w-5 h-5" /></div>
          <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide font-semibold">Total balance</div>
          <div className="mt-1 font-display text-2xl font-bold tabular text-emerald-700" data-testid="dist-wallet-balance">{fmtPKR(data.wallet_balance)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="w-10 h-10 rounded-md border border-amber-200 bg-amber-50 text-amber-700 flex items-center justify-center"><Hourglass className="w-5 h-5" /></div>
          <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide font-semibold">Pending withdraw</div>
          <div className="mt-1 font-display text-2xl font-bold tabular text-amber-700" data-testid="dist-wallet-pending">{fmtPKR(data.pending_withdraw)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="w-10 h-10 rounded-md border border-sky-200 bg-sky-50 text-sky-700 flex items-center justify-center"><WalletIcon className="w-5 h-5" /></div>
          <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide font-semibold">Available</div>
          <div className="mt-1 font-display text-2xl font-bold tabular" data-testid="dist-wallet-available">{fmtPKR(data.available)}</div>
        </div>
      </div>

      <form onSubmit={saveBank} className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="dist-bank-form">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-emerald-600" />
          <div className="font-display font-bold text-lg">Bank / Mobile Wallet Details</div>
        </div>
        <p className="text-xs text-slate-500">Yeh details admin ko aap ko payment bhejne ke liye chahiye. Aap ke records mein safe rakhi jaati hain.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {BANK_FIELDS.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label>{f.label}</Label>
              <Input value={bank[f.key] || ""} onChange={(e) => setBank({ ...bank, [f.key]: e.target.value })} data-testid={`dist-bank-${f.key}`} />
            </div>
          ))}
        </div>
        <Button type="submit" disabled={bankBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dist-bank-save">
          <Save className="w-4 h-4 mr-1" /> {bankBusy ? "Saving…" : "Save bank details"}
        </Button>
      </form>

      <div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-sky-600 hover:bg-sky-700 text-white" disabled={data.available <= 0} data-testid="dist-withdraw-btn">
              <ArrowDownToLine className="w-4 h-4 mr-1" /> Request Withdrawal
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="dist-withdraw-dialog">
            <DialogHeader><DialogTitle>Request withdrawal</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Amount (PKR) — available {fmtPKR(data.available)}</Label>
                <Input type="number" required min={1} max={data.available} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="wd-amount" />
              </div>
              <div className="space-y-1.5">
                <Label>Note (optional)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="wd-note" />
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="wd-submit">Submit</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <form
        onSubmit={saveWa}
        className="bg-white border border-slate-200 rounded-md p-6 space-y-4"
        data-testid="dist-wa-card"
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-600" />
            <div>
              <div className="font-display font-bold text-lg">WhatsApp Business Settings</div>
              <p className="text-xs text-slate-500">
                Apna WhatsApp number connect karein — referrals share karein, leads attract karein, support se baat karein.
              </p>
            </div>
          </div>
          <WaStatusPill connected={waConnected} testIdPrefix="dist-wa" />
        </div>
        <div className="space-y-1.5">
          <Label>Distributor WhatsApp Number</Label>
          <Input
            placeholder="03001234567"
            value={waNumber}
            onChange={(e) => setWaNumber(e.target.value)}
            inputMode="numeric"
            data-testid="dist-wa-input"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={waBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dist-wa-save">
            <Save className="w-4 h-4 mr-1.5" /> {waBusy ? "Saving..." : "Save Number"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={openSupport}
            data-testid="dist-wa-contact-support"
          >
            <MessageCircle className="w-4 h-4 mr-1.5 text-emerald-600" /> Contact Support
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={shareReferralViaWhatsApp}
            data-testid="dist-wa-share-referral"
          >
            <Share2 className="w-4 h-4 mr-1.5 text-emerald-600" /> Share Referral via WhatsApp
            <ExternalLink className="w-3 h-3 ml-1.5 text-slate-500" />
          </Button>
        </div>
        {me?.referral_code && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 font-mono break-all">
            Referral link: <span className="text-slate-800">{referralLink}</span>
          </div>
        )}
      </form>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <div className="p-4 border-b border-slate-200 font-display font-semibold">Withdrawal history</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Date</th>
              <th className="text-right px-4 py-3 font-semibold">Amount</th>
              <th className="text-left px-4 py-3 font-semibold">Note</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-left px-4 py-3 font-semibold">Settled</th>
            </tr>
          </thead>
          <tbody>
            {data.withdrawals.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No withdrawals yet.</td></tr>}
            {data.withdrawals.map((w) => (
              <tr key={w.id} className="border-t border-slate-100" data-testid={`dist-wd-row-${w.id}`}>
                <td className="px-4 py-3 tabular text-xs">{(w.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3 text-right tabular font-semibold">{fmtPKR(w.amount)}</td>
                <td className="px-4 py-3 text-slate-600">{w.note || "—"}</td>
                <td className="px-4 py-3"><span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold border ${STATUS_TONE[w.status] || "border-slate-200"}`}>{w.status}</span></td>
                <td className="px-4 py-3 tabular text-xs">{w.settled_at ? w.settled_at.slice(0, 10) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
